<template>
  <v-btn
    class="app-btn"
    :class="[`app-btn--${variant}`, `app-btn--${size}`, { 'app-btn--light': lightMode }]"
    v-bind="bindings"
    v-on="$listeners"
  >
    <!-- 슬롯 투명 전달 (loader 제외 — 아래 기본 점 로더와 충돌 방지) -->
    <template v-for="(_, name) in passthroughSlots" #[name]="slotData"><slot :name="name" v-bind="slotData"></slot></template>
    <!-- 기본 로더 = 통통 튀는 점 3개 (#loader 슬롯으로 교체 가능) -->
    <template #loader>
      <slot name="loader">
        <span class="app-btn__dots"><i></i><i></i><i></i></span>
      </slot>
    </template>
  </v-btn>
</template>

<script>
import Themeable from "vuetify/lib/mixins/themeable";

// v-btn 래퍼 — App* 톤(다크 NLE 기본, light prop). ripple/loading/disabled/아이콘 등 v-btn 기능 그대로.
//  variant: primary(채움 보라) | outline(보라 테두리) | ghost(뮤트 텍스트) | danger(위험/삭제)
export default {
  name: "AppButton",
  mixins: [Themeable],
  inheritAttrs: false,
  props: {
    variant: {
      type: String,
      default: "primary",
      validator: (v) => ["primary", "outline", "ghost", "danger"].includes(v),
    },
    // 사이즈 토큰: xs(22px) | sm(28px, 기본) | md(34px)
    size: {
      type: String,
      default: "sm",
      validator: (v) => ["xs", "sm", "md"].includes(v),
    },
  },
  computed: {
    lightMode() {
      return !this.isDark;
    },
    bindings() {
      return { small: true, depressed: true, ...this.$attrs };
    },
    passthroughSlots() {
      const { loader, ...rest } = this.$scopedSlots;
      return rest;
    },
  },
};
</script>

<style scoped>
.app-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-size: 12px !important;
  font-weight: 600;
  border-radius: 7px;
  padding: 0 12px !important;
}

/* ── 사이즈 토큰 ── */
.app-btn--xs {
  height: 22px !important;
  padding: 0 8px !important;
  font-size: 11px !important;
  border-radius: 6px;
}
.app-btn--xs .app-btn__dots i {
  width: 4px;
  height: 4px;
}
/* sm = 기본 (v-btn small, 28px) */
.app-btn--md {
  height: 34px !important;
  padding: 0 16px !important;
  font-size: 13px !important;
}

/* ── primary: 채움 보라 ── */
.app-btn--primary {
  background-color: #7c4dff !important;
  color: #fff !important;
}
.app-btn--primary:hover {
  background-color: #8f66ff !important;
}

/* ── outline: 보라 테두리 ── */
.app-btn--outline {
  background-color: transparent !important;
  border: 1px solid rgba(145, 85, 253, 0.55);
  color: #b79cff !important;
}
.app-btn--outline:hover {
  background-color: rgba(145, 85, 253, 0.12) !important;
  border-color: rgba(145, 85, 253, 0.8);
}

/* ── ghost: 뮤트 텍스트 ── */
.app-btn--ghost {
  background-color: transparent !important;
  color: #9a93ad !important;
}
.app-btn--ghost:hover {
  background-color: rgba(145, 85, 253, 0.1) !important;
  color: #cfc8e6 !important;
}

/* ── danger: 위험/삭제 ── */
.app-btn--danger {
  background-color: transparent !important;
  border: 1px solid rgba(255, 90, 90, 0.5);
  color: #ff7d7d !important;
}
.app-btn--danger:hover {
  background-color: rgba(255, 90, 90, 0.12) !important;
  border-color: rgba(255, 90, 90, 0.8);
}

/* (외부 #loader 슬롯으로 원형 스피너를 쓸 경우 대비) 버튼(28px)에 맞게 축소 */
.app-btn ::v-deep .v-progress-circular {
  width: 14px !important;
  height: 14px !important;
}
/* 기본 로더: 통통 튀는 점 3개 (색은 variant의 currentColor 상속) */
.app-btn__dots {
  display: flex;
  align-items: center;
  gap: 3px;
}
.app-btn__dots i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  animation: app-btn-bounce 0.55s infinite alternate ease-in-out;
}
.app-btn__dots i:nth-child(2) {
  animation-delay: 0.14s;
}
.app-btn__dots i:nth-child(3) {
  animation-delay: 0.28s;
}
@keyframes app-btn-bounce {
  from {
    transform: translateY(2px);
    opacity: 0.45;
  }
  to {
    transform: translateY(-3px);
    opacity: 1;
  }
}

/* 비활성 (loading 중엔 원래 variant 색 유지 — v-btn은 loading 시 disabled 클래스도 붙음) */
.app-btn.v-btn--disabled:not(.v-btn--loading) {
  opacity: 0.45;
  background-color: transparent !important;
  color: #6b6b7d !important;
}
.app-btn--primary.v-btn--disabled:not(.v-btn--loading) {
  background-color: #2f2f3d !important;
}

/* ── light theme ── */
.app-btn--light.app-btn--primary {
  /* primary는 동일 (보라 채움) */
  box-shadow: 0 2px 8px rgba(124, 77, 255, 0.25);
}
.app-btn--light.app-btn--outline {
  border-color: rgba(124, 77, 255, 0.45);
  color: #7c4dff !important;
}
.app-btn--light.app-btn--outline:hover {
  background-color: rgba(124, 77, 255, 0.07) !important;
  border-color: rgba(124, 77, 255, 0.75);
}
.app-btn--light.app-btn--ghost {
  color: #8a84a0 !important;
}
.app-btn--light.app-btn--ghost:hover {
  background-color: rgba(124, 77, 255, 0.07) !important;
  color: #4b4462 !important;
}
.app-btn--light.app-btn--danger {
  border-color: rgba(229, 57, 53, 0.45);
  color: #e53935 !important;
}
.app-btn--light.app-btn--danger:hover {
  background-color: rgba(229, 57, 53, 0.07) !important;
  border-color: rgba(229, 57, 53, 0.75);
}
.app-btn--light.v-btn--disabled:not(.v-btn--loading) {
  color: #b9b4c9 !important;
}
.app-btn--light.app-btn--primary.v-btn--disabled:not(.v-btn--loading) {
  background-color: #ece9f5 !important;
  box-shadow: none;
}
</style>
